import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Divider,
  Avatar,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Menu as MenuIcon, AccountCircle, Logout, Login, PersonAdd, PersonOutline, WorkspacePremium, Settings as SettingsIcon, Search as SearchIcon, BookmarkBorder, InfoOutlined, ContactSupportOutlined } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStaff } from '../contexts/StaffContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { usePremiumStatus } from '../hooks/usePremiumStatus';
import PremiumContactIcon from './PremiumContactIcon';
import MarqueeBanner from './MarqueeBanner';
import styles from './Navbar.module.css';
import { isHighSchoolAuthRoute } from '../utils/authUtils';
import normalizeImageUrl from '../utils/normalizeImageUrl';
import api from '../api';

interface NavbarProps {
  onToggleSidebar?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onToggleSidebar }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { staff, logout: staffLogout } = useStaff();
  const { openLogin, openSignup } = useAuthModal();
  const [adminUser, setAdminUser] = useState<{ name?: string; email?: string } | null>(null);
  const { isPremium } = usePremiumStatus();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isSchoolRoute = location.pathname === '/school' || location.pathname.startsWith('/school/');
  const isParentRoute = location.pathname.startsWith('/parent');
  const isStaffRoute = location.pathname.startsWith('/staff');
  const isStaffAuth = location.pathname === '/staff/login';
  const isSchoolWorkRoute = location.pathname === '/school-work';
  const isHighSchoolAuth = isHighSchoolAuthRoute(location.pathname);
  let effectiveUser = user;
  if (!effectiveUser) {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) effectiveUser = JSON.parse(storedUser);
    } catch {
      // Continue using the authenticated context when stored user data is invalid.
    }
  }
  const isHighSchoolUser = Boolean(
    effectiveUser?.educationLevel === 'high-school' ||
    effectiveUser?.school ||
    effectiveUser?.schoolName ||
    effectiveUser?.schoolEmail ||
    effectiveUser?.registrationId ||
    effectiveUser?.className
  );
  const isHighSchoolExperience = isHighSchoolUser ||
    location.pathname.startsWith('/high-school') ||
    location.pathname.startsWith('/mock-test') ||
    location.pathname.startsWith('/school-work') ||
    location.pathname.startsWith('/waec') ||
    location.pathname.startsWith('/jamb') ||
    location.pathname === '/school' ||
    location.pathname.startsWith('/school/') ||
    location.pathname.startsWith('/parent/') ||
    location.pathname.startsWith('/staff/');
  const isResearchSearchRoute = location.pathname === '/projects' ||
    location.pathname.startsWith('/projects/') ||
    location.pathname === '/blog' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname === '/knowledge-base' ||
    location.pathname.startsWith('/knowledge-base/') ||
    location.pathname === '/research-areas';
  const useSchoolSearch = !isResearchSearchRoute && (
    isHighSchoolUser ||
    location.pathname.startsWith('/high-school') ||
    location.pathname.startsWith('/waec') ||
    location.pathname.startsWith('/jamb') ||
    location.pathname.startsWith('/school-work') ||
    location.pathname.startsWith('/mock-test') ||
    location.pathname.startsWith('/school-search')
  );
  const isAuthRoute = /(^|\/)(login|signup)(\/|$)/.test(location.pathname) || isHighSchoolAuth || isStaffAuth;
  const bookmarksLink = isHighSchoolUser ? '/school-work/bookmarks' : '/bookmarks';
  const aboutLink = isHighSchoolExperience ? '/high-school/about' : '/about';
  const logoLink = isAdminRoute ? '/admin' : isSchoolRoute ? '/school/dashboard' : isParentRoute ? '/parent/dashboard' : isSchoolWorkRoute ? '/' : '/';
  const logoProps = isAuthRoute ? {} : { component: Link as any, to: logoLink };
  
  const handleAdminLogout = async () => {
    handleMenuClose();
    const token = localStorage.getItem('admin_auth_token');
    try {
      if (token) await api.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      // Local sign-out still proceeds if the server is unavailable.
    }
    // CRITICAL: Clear all auth (admin AND public user) to prevent sessions from interfering
    try {
      localStorage.removeItem('admin_auth_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Clear per-tab admin unlocks
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.startsWith('admin_tab_pw_')) keysToRemove.push(k);
        }
        keysToRemove.forEach(k => sessionStorage.removeItem(k));
      } catch (e) {
        // ignore
      }
    } catch (e) {
      // ignore any errors during cleanup
    }
    setAdminUser(null);
    navigate('/admin/login');
  };

  const handleStaffLogout = () => {
    handleMenuClose();
    staffLogout();
    navigate('/staff/login');
  };

  const handleUserLogout = () => {
    handleMenuClose();
    const shouldRedirectToSchoolLogin = Boolean(
      user?.educationLevel === 'high-school' ||
      user?.school ||
      user?.schoolName ||
      user?.schoolEmail
    );

    void logout().finally(() => {
      if (shouldRedirectToSchoolLogin) {
        navigate('/high-school/login');
      } else {
        openLogin();
      }
    });
  };
  
  // Use 'sm' breakpoint so the permanent sidebar remains for most desktop/tablet sizes
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load admin user from localStorage only when on admin routes
  useEffect(() => {
    try {
      const raw = localStorage.getItem('admin_user');
      if (raw && location.pathname.startsWith('/admin')) {
        setAdminUser(JSON.parse(raw));
      } else {
        setAdminUser(null);
      }
    } catch (e) {
      setAdminUser(null);
    }
  }, [location.pathname]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(useSchoolSearch ? `/school-search?q=${encodeURIComponent(searchQuery)}` : `/projects?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  return (
    <>
      <MarqueeBanner />
      <AppBar position="sticky" elevation={0} sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1, backgroundColor: 'var(--surface)', color: 'var(--ink)', borderBottom: '1px solid var(--border)', borderRadius: 0, marginLeft: 0, width: '100%', className: styles["navbar"] })}>
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 } }}>
          {/* Sidebar toggle for desktop */}
          {!isAdminRoute && !isAuthRoute && !isSchoolRoute && !isStaffRoute && !isMobile && onToggleSidebar && (
            <IconButton 
              onClick={onToggleSidebar} 
              sx={{ color: 'var(--ink)', mr: 1 }} 
              aria-label="toggle sidebar"
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* Hamburger for mobile */}
          {!isAdminRoute && !isAuthRoute && !isSchoolRoute && !isStaffRoute && isMobile && onToggleSidebar && (
            <IconButton edge="start" onClick={onToggleSidebar} sx={{ color: 'var(--ink)', mr: 1 }} aria-label="open drawer">
              <MenuIcon />
            </IconButton>
          )}

          <Box 
            {...logoProps}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1, 
              textDecoration: 'none', 
              marginLeft: { xs: 0, sm: 2, md: 8 },
              minWidth: 0, // Allow shrinking on mobile
              cursor: isAuthRoute ? 'default' : 'pointer',
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo_with_bottom_text.svg`}
              alt="A3Tech Education"
              style={{
                height: isMobile ? 48 : 56,
                width: isMobile ? 72 : 84,
                marginRight: isMobile ? 6 : 12,
                objectFit: 'contain',
              }}
            />
          </Box>
          {/* Search Bar - hidden on very small screens and on HS auth pages */}
          {!isHighSchoolAuth && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1, maxWidth: { md: 400, lg: 500 }, mx: { md: 1, lg: 2 } }}>
              <TextField
              placeholder="Search WAEC, JAMB, topics..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearch}
              sx={{
                width: '100%',
                backgroundColor: 'var(--surface)',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  color: 'var(--ink)',
                  backgroundColor: 'var(--surface)',
                  '& fieldset': {
                    borderColor: 'var(--border)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--primary)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--primary)',
                  },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: 'var(--muted)',
                  opacity: 1,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (searchQuery.trim()) {
                          navigate(useSchoolSearch ? `/school-search?q=${encodeURIComponent(searchQuery)}` : `/projects?search=${encodeURIComponent(searchQuery)}`);
                          setSearchQuery('');
                        }
                      }}
                      sx={{ color: 'var(--ink)' }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2, md: 3 }, marginRight: { xs: 1, sm: 2, md: 8 } }}>
            {/* User greeting */}
            {((isAuthenticated && !isStaffRoute) || adminUser || staff) && !isSchoolRoute ? (
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: 'var(--ink)', 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  Welcome, <strong>{
                    isAdminRoute ? adminUser?.name || 'Admin'
                    : isStaffRoute ? staff?.name || 'Staff'
                    : (user?.educationLevel === 'high-school' && !isParentRoute) ? user?.name || 'Student'
                    : isParentRoute ? user?.parentName || user?.name || 'User'
                    : user?.name || user?.parentName || 'User'
                  }</strong>
                </Typography>
                {isParentRoute && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                    <Button component={Link} to="/parent/dashboard" size="small" sx={{ color: 'var(--ink)', borderColor: 'var(--border)', borderWidth: 1, borderStyle: 'solid', padding: '4px 10px' }}>
                      Parent Dashboard
                    </Button>
                    <Button component={Link} to="/parent/tickets" size="small" sx={{ color: 'var(--ink)', borderColor: 'var(--border)', borderWidth: 1, borderStyle: 'solid', padding: '4px 10px' }}>
                      Support Tickets
                    </Button>
                  </Box>
                )}
                {!isAdminRoute && !isStaffRoute && !isSchoolRoute && (user?.className || user?.registrationId || (isHighSchoolUser && user?.department)) ? (
                  <>
                    {isHighSchoolUser && user?.department && (
                      <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mt: 0.25 }}>
                        Department: {user.department}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'var(--muted)',
                        display: 'block',
                        mt: 0.25,
                      }}
                    >
                      Class: {user.className}
                    </Typography>
                    {user?.registrationId && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--muted)',
                          display: 'block',
                          mt: 0.25,
                        }}
                      >
                        ID: {user.registrationId}
                      </Typography>
                    )}
                  </>
                ) : null}
              </Box>
            ) : null}

            {((isAuthenticated && !isStaffRoute) || adminUser || staff) && !isSchoolRoute ? (
              <>
                {isHighSchoolUser && user?.department && (
                  <Typography
                    variant="caption"
                    sx={{ display: { xs: 'block', lg: 'none' }, color: 'var(--ink)', fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    Dept: {user.department}
                  </Typography>
                )}
                {/* User or Staff avatar/menu button */}
                <IconButton onClick={handleMenuOpen} sx={{ color: 'var(--ink)' }}>
                  {isAdminRoute ? (
                    // Admin route - show admin avatar
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        bgcolor: 'primary.main',
                        color: '#fff'
                      }}
                    >
                      {adminUser?.name?.charAt(0).toUpperCase() || 'A'}
                    </Avatar>
                  ) : isStaffRoute ? (
                    staff?.name ? (
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32,
                          bgcolor: 'primary.main',
                          color: '#fff'
                        }}
                      >
                        {staff.name.charAt(0).toUpperCase()}
                      </Avatar>
                    ) : (
                      <AccountCircle fontSize="large" />
                    )
                  ) : (
                    // Regular route - show user avatar with premium status
                    user?.name ? (
                      <Box sx={{ position: 'relative' }}>
                        <Avatar 
                          src={normalizeImageUrl(user.profilePhoto) || user.profilePhoto}
                          alt={user.name}
                          sx={{ 
                            width: 32, 
                            height: 32,
                            bgcolor: isPremium ? 'warning.main' : 'primary.main',
                            color: isPremium ? 'text.primary' : 'inherit'
                          }}
                        >
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                        {isPremium && (
                          <PremiumContactIcon 
                            sx={{ 
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              width: 16,
                              height: 16,
                            }} 
                          />
                        )}
                      </Box>
                    ) : (
                      <AccountCircle fontSize="large" />
                    )
                  )}
                </IconButton>
                {/* User menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {!isStaffRoute && (
                    <MenuItem onClick={handleMenuClose}>
                      <ListItemIcon>
                        {isAdminRoute ? (
                          <WorkspacePremium fontSize="small" sx={{ color: 'primary.main' }} />
                        ) : isPremium ? (
                          <WorkspacePremium fontSize="small" sx={{ color: 'warning.main' }} />
                        ) : (
                          <PersonOutline fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>
                              {isAdminRoute ? adminUser?.name || 'Admin' : user?.name || 'Profile'}
                            </span>
                            {!isAdminRoute && isPremium && (
                              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                                PREMIUM
                              </Typography>
                            )}
                            {isAdminRoute && (
                              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                ADMIN
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={isAdminRoute ? adminUser?.email : user?.email} 
                      />
                    </MenuItem>
                  )}
                  {!isStaffRoute && <Divider />}
                  {isAdminRoute ? (
                    // Admin menu items
                    <MenuItem onClick={handleAdminLogout}>
                      <ListItemIcon>
                        <Logout fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Admin Logout" />
                    </MenuItem>
                  ) : isStaffRoute ? (
                    <>
                      <MenuItem component={Link} to="/staff/dashboard" onClick={handleMenuClose}>
                        <ListItemIcon>
                          <PersonOutline fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Profile" />
                      </MenuItem>
                      <MenuItem onClick={handleStaffLogout}>
                        <ListItemIcon>
                          <Logout fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Logout" />
                      </MenuItem>
                    </>
                  ) : (
                    // Regular user menu items
                    <>
                        <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
                          <ListItemIcon><PersonOutline fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Profile" />
                        </MenuItem>
                        <MenuItem component={Link} to={bookmarksLink} onClick={handleMenuClose}>
                          <ListItemIcon><BookmarkBorder fontSize="small" /></ListItemIcon>
                          <ListItemText primary="My Bookmarks" />
                        </MenuItem>
                        <MenuItem component={Link} to="/settings" onClick={handleMenuClose}>
                          <ListItemIcon>
                            <SettingsIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Settings" />
                        </MenuItem>
                        <Divider />
                      <MenuItem component={Link} to={aboutLink} onClick={handleMenuClose}>
                        <ListItemIcon><InfoOutlined fontSize="small" /></ListItemIcon>
                        <ListItemText primary="About" />
                      </MenuItem>
                      <MenuItem component={Link} to="/high-school/contact" onClick={handleMenuClose}>
                        <ListItemIcon><ContactSupportOutlined fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Contact" />
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleUserLogout}>
                        <ListItemIcon>
                          <Logout fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Logout" />
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </>
            ) : !isAdminRoute && !isHighSchoolAuth && !isSchoolRoute && !isAuthRoute && (
              <>
                {/* Login/Signup buttons (hide on admin routes, HS auth pages, and school pages) */}
                <Button
                  startIcon={<PersonAdd />}
                  onClick={openSignup}
                  sx={{ 
                    color: 'var(--primary)', 
                    borderColor: 'var(--primary)',
                    border: '1px solid',
                    backgroundColor: 'var(--surface)',
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '4px 8px', sm: '6px 16px' },
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0.5, sm: 1 },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Sign up</Box>
                </Button>
                <Button
                  startIcon={<Login />}
                  onClick={openLogin}
                  sx={{ 
                    color: 'var(--primary)', 
                    borderColor: 'var(--primary)',
                    border: '1px solid',
                    backgroundColor: 'var(--surface)',
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '4px 8px', sm: '6px 16px' },
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0.5, sm: 1 },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Login</Box>
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
}

export default Navbar;
